import React, { useEffect } from "react";
import { API_SUCCESS } from "../../../common/constants/ApiConstants";
import { DISABILITY_OR_SOCIAL_SECURITY } from "../../../models/requestType";
import { RequestorFormConstants } from "./DataEntryFieldsConstants";
import { Requestor } from "../../../models/requestor";
import { RequestPurpose } from "../../../models/requestType";

export interface DataEntryDetailsRightPaneRequestorSectionProps {
    readonly activeTabId: number;
    readonly requestType?: number;
    readonly canEditPatient?: boolean;
    /**
     * 'requestorModal' when this section is rendered inside Quick Entry's reused
     * requestor-search modal; undefined for Classic's own real tab usage. Only the
     * modal embedding needs the noBarcodeValue touched-guard below (see its comment) -
     * Classic mounts this section exactly once per genuine transaction open, so it
     * never has anything to protect against and must always populate on a new scan.
     */
    readonly hostContext?: string;
    /**
     * redux-form's real untouched value for this checkbox is "" (empty string), not
     * undefined - hence the string union, not just boolean|undefined. See the
     * scan-repopulate effect below.
     */
    readonly noBarcodeValue?: boolean | string;
    readonly transactionDdsSsn?: any;
    readonly ssnValue?: string;
    readonly requestorDetails?: Requestor;
    readonly requestPurpose?: RequestPurpose;
    readonly organizationId?: number;
    readonly transactionId?: string;
    readonly transactionDDS?: any;
    readonly readBarCodeDDSAPIStatus?: any;
    readonly changeFieldValue: (fieldName: string, value: any) => void;
    readonly setDisableField: (value: boolean) => void;
    readonly setDisableDDSField: (value: boolean) => void;
    readonly setLastUsedRequestorInfo: (data: any) => void;
    readonly setErrorMessage: (value: string) => void;
    readonly setDOBError: (value: string) => void;
    readonly setLastNameError: (value: string) => void;
}

/**
 * Requestor-tab-scoped effects extracted from DataEntryDetailsRightPane.tsx
 * (Spec #34098 / F1a, AC13, Business Rule 16 - tidy-first structural refactor
 * #1). Headless by design: state ownership stays with the RightPane
 * orchestrator (passed down as props/setters) so behavior is unchanged -
 * only the *location* of these effects moves. Always rendered by the
 * orchestrator regardless of activeTabId, matching the pre-refactor firing
 * semantics.
 */
const DataEntryDetailsRightPaneRequestorSection: React.FC<DataEntryDetailsRightPaneRequestorSectionProps> = (props) => {
    useEffect(() => {
        props.setDisableField(!props.canEditPatient);
    }, [props.canEditPatient]);

    useEffect(() => {
        if (!props.noBarcodeValue && props.transactionDdsSsn) {
            if (props.ssnValue) {
                props.changeFieldValue(RequestorFormConstants.SSN, '***-**-***');
            }
        }
    }, [props.activeTabId === 2 && props.requestType == DISABILITY_OR_SOCIAL_SECURITY]);

    useEffect(() => {
        if (props.requestorDetails?.requestorInfo?.id) {
            props.setLastUsedRequestorInfo({
                requestorName: props.requestorDetails?.fullName,
                address1: props.requestorDetails?.requestorAddress?.address1,
                address2: props.requestorDetails?.requestorAddress?.address2,
                city: props.requestorDetails?.requestorAddress?.city,
                state: props.requestorDetails?.requestorAddress?.state,
                zip: props.requestorDetails?.requestorAddress?.zipCode,
                firstName: props.requestorDetails?.requestorInfo?.firstName,
                lastName: props.requestorDetails?.requestorInfo?.lastName,
                email: props.requestorDetails?.requestorInfo?.email,
                phone: props.requestorDetails?.requestorInfo?.phone,
                fax: props.requestorDetails?.requestorInfo?.fax,
                requestorContactId: props.requestorDetails?.requestorInfo?.id,
                requestorId: props.requestorDetails?.requestorID,
                requestPurpose: props.requestPurpose,
                org: props.organizationId,
                transactionId: props.transactionId,
            });
        }
    }, [props.requestorDetails?.requestorInfo?.id]);

    useEffect(() => {
        // Bug fix (2026-07-29, second pass): the guard below is ONLY meaningful for
        // Quick Entry's requestor-search modal (hostContext === 'requestorModal'),
        // which mounts a FRESH copy of this section every time it opens (Reset,
        // picking a new requestor, etc.) - see the guard's own comment for why an
        // explicit `false` there means "the specialist already unchecked this,
        // don't silently restore the scan over it."
        //
        // Classic Mode has no such remount: this section mounts exactly ONCE per
        // genuine transaction open, before the specialist has touched anything, so
        // there is nothing to protect against - and Classic's initial noBarcodeValue
        // is NEVER redux-form's untouched "" sentinel to begin with. It is always the
        // backend's real boolean (transactionDetails.transactionDds.noBarcode is
        // unconditionally populated server-side - TransactionDataAccess.cs), `false`
        // for the overwhelming majority of DDS transactions that DO use a barcode.
        // Applying the touched-guard in Classic therefore silently dropped every
        // scan's six fields the instant the pane mounted, before the specialist ever
        // saw them - which is exactly the reported regression (API call succeeds,
        // fields stay empty, checkbox shows correctly unchecked). Scoping the guard
        // to the modal restores Classic's original, always-populate-on-a-new-scan
        // behavior while keeping Quick Entry's protection intact.
        if (props.hostContext === 'requestorModal' && props.noBarcodeValue !== undefined && props.noBarcodeValue !== "") {
            return;
        }
        if (props.transactionDDS?.rqid) {
            props.changeFieldValue(RequestorFormConstants.RQID, props.transactionDDS?.rqid);
        }
        if (props.transactionDDS?.txSiteCode) {
            props.changeFieldValue(RequestorFormConstants.TX_SITE_CODE, props.transactionDDS?.txSiteCode);
            props.changeFieldValue(RequestorFormConstants.RQ_SITE_CODE, props.transactionDDS?.txSiteCode);
        }
        if (props.transactionDDS?.docReturnCode) {
            props.changeFieldValue(RequestorFormConstants.DOC_RETURN_CODE, props.transactionDDS?.docReturnCode);
        }
        if (props.transactionDDS?.ssn) {
            props.changeFieldValue(RequestorFormConstants.SSN, '***-**-****');
        }
        if (props.transactionDDS?.docType) {
            props.changeFieldValue(RequestorFormConstants.DOC_TYPE, props.transactionDDS?.docType);
        }
        if (props.transactionDDS?.routingField) {
            props.changeFieldValue(RequestorFormConstants.ROUTING_FIELD, props.transactionDDS?.routingField);
        }
    }, [props.transactionDDS, props.readBarCodeDDSAPIStatus == API_SUCCESS]);

    useEffect(() => {
        props.setDisableDDSField(Boolean(props.noBarcodeValue));
    }, [props.noBarcodeValue]);

    // Additive: mirrors the search-error-reset portion of the pre-refactor
    // orchestrator-level [activeTabId] effect (RightPane.tsx) for this
    // section's own error state (Test 5.5). The orchestrator's original
    // effect is left in place (Business Rule 16 only names the 3
    // cross-cutting effects as staying there; this reset is idempotent so
    // running it from both places is behavior-preserving).
    useEffect(() => {
        props.setErrorMessage("");
        props.setDOBError("");
        props.setLastNameError("");
    }, [props.activeTabId]);

    return null;
};

export default DataEntryDetailsRightPaneRequestorSection;
